
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiFetch } from '../../api';
const API_BASE_URL = (() => { const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, ''); return base.endsWith('/api/v1') ? base : base + '/api/v1'; })();

import {
    X, GitMerge, Plus, Trash2, Check,
    ArrowRight, ArrowLeft, ArrowLeftRight, Circle,
    Calendar, Sparkles, Search, AlertTriangle, HelpCircle, Database,
    Wand2, Loader2, Grid
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notify } from '@/components/core';

interface JoinConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    nodeData: any;
    inputColumns: any[];
    onUpdate: (key: string, value: any) => void;
    leftSourceId?: string;
    rightSourceId?: string;
    leftSourceName?: string;
    rightSourceName?: string;
    onDebugSql?: () => void;
    getNodes?: () => any[];
}

const getToken = () => {
    try {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        if (token) return token;

        const storage = localStorage.getItem('auth-storage');
        if (storage) return JSON.parse(storage).state?.token || null;
    } catch (e) {
        console.error("Failed to parse auth token", e);
    }
    return null;
};

export const JoinConfigModal = ({ isOpen, onClose, nodeData, inputColumns, onUpdate, leftSourceId, rightSourceId, onDebugSql, getNodes }: JoinConfigModalProps) => {
    // NOTE: Do NOT put early return here — hooks must always be called in the same order.
    // The isOpen guard is moved to just before the return statement below.

    // Get actual source node names for display
    // Note: leftSourceId/rightSourceId may be undefined from PropertiesPanel
    // Instead, we'll use the 'source' property from inputColumns

    // Group columns by their 'source' property ('left' or 'right')
    const leftColumns = inputColumns.filter(c => c.source === 'left');
    const rightColumns = inputColumns.filter(c => c.source === 'right');

    // Use sourceId embedded in columns (set by PropertiesPanel propagation) — more reliable than props
    const leftNodeId = leftColumns[0]?.sourceId || leftSourceId;
    const rightNodeId = rightColumns[0]?.sourceId || rightSourceId;

    // Resolve actual upstream nodes
    const allNodes = getNodes ? getNodes() : [];
    const leftNode = leftNodeId ? allNodes.find((n: any) => n.id === leftNodeId) : null;
    const rightNode = rightNodeId ? allNodes.find((n: any) => n.id === rightNodeId) : null;

    // Resolve a meaningful display name from node data
    const getNodeName = (node: any, fallback: string) => {
        if (!node) return fallback;
        const d = node.data || {};
        if (d.label) return d.label;
        if (d.table) return d.table;
        if (d.objectName) return d.objectName;
        if (d.alias) return d.alias;
        if (d.isCustomSql && d.customSql) return 'Custom SQL';
        if (node.type) return node.type.charAt(0).toUpperCase() + node.type.slice(1);
        return fallback;
    };

    const leftSourceName = getNodeName(leftNode, leftColumns.length > 0 ? 'Left Source' : 'Not Connected');
    const rightSourceName = getNodeName(rightNode, rightColumns.length > 0 ? 'Right Source' : 'Not Connected');



    const joinType = nodeData.joinType || 'INNER';
    const conditions = nodeData.conditions || [];
    const temporalJoin = nodeData.temporalJoin || {};
    const riskMonitor = nodeData.riskMonitor || {};
    const inspector = nodeData.inspector || {}; // Initialize inspector variable

    const [searchTerm, setSearchTerm] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasAutoMatched, setHasAutoMatched] = useState(false);

    // Local state for batching updates - only sync to parent on close
    const [localConditions, setLocalConditions] = useState(conditions);
    const [localJoinType, setLocalJoinType] = useState(joinType);
    const [localTemporalJoin, setLocalTemporalJoin] = useState(temporalJoin);
    const [localRiskMonitor, setLocalRiskMonitor] = useState(riskMonitor);
    const [localInspector, setLocalInspector] = useState(inspector);
    const [localColumns, setLocalColumns] = useState(nodeData.columns || []);

    // Sync props → local state ONLY when modal transitions closed→open.
    // Using a ref to track previous isOpen prevents accidental resets when the
    // parent re-renders (e.g. calling onUpdate) while the modal is already open.
    const prevIsOpenRef = useRef(false);
    useEffect(() => {
        const wasOpen = prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;
        if (isOpen && !wasOpen) {
            // Modal just opened: load the latest saved node values
            setLocalConditions(nodeData.conditions || []);
            setLocalJoinType(nodeData.joinType || 'INNER');
            setLocalTemporalJoin(nodeData.temporalJoin || {});
            setLocalRiskMonitor(nodeData.riskMonitor || {});
            setLocalInspector(nodeData.inspector || {});
            setLocalColumns(nodeData.columns || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]); // ONLY re-sync from props when modal opens/closes, NOT on every prop change

    // Update getColsForSide to use 'source' property from columns
    const getColsForSide = useCallback((side: 'left' | 'right') => {
        return inputColumns.filter(c => c.source === side);
    }, [inputColumns]);

    // Auto-Match on Open - with flag to prevent re-running
    useEffect(() => {
        if (isOpen && !hasAutoMatched && localConditions.length === 0 && inputColumns.length > 0 && leftSourceId && rightSourceId) {
            const leftCols = getColsForSide('left');
            const rightCols = getColsForSide('right');

            // Find simple matches by name
            const matches: any[] = [];
            leftCols.forEach((lc: any) => {
                const match = rightCols.find((rc: any) => rc.name.toLowerCase() === lc.name.toLowerCase());
                if (match) {
                    matches.push({ leftCol: lc.name, operator: '=', rightCol: match.name });
                }
            });

            if (matches.length > 0) {
                setLocalConditions(matches);
                onUpdate('conditions', matches);
                setHasAutoMatched(true);
                notify.success(`Auto-matched ${matches.length} columns: ${matches.map((m: any) => m.leftCol).join(', ')}`);
            }
        }

        // Reset auto-match flag when modal closes
        if (!isOpen && hasAutoMatched) {
            setHasAutoMatched(false);
        }
    }, [isOpen, localConditions.length, inputColumns.length, leftSourceId, rightSourceId, hasAutoMatched, getColsForSide]);

    const handleAddCondition = () => {
        const newConds = [...localConditions, { leftCol: '', operator: '=', rightCol: '' }];
        setLocalConditions(newConds);
        onUpdate('conditions', newConds);
    };

    const handleRemoveCondition = (index: number) => {
        const newConds = localConditions.filter((_: any, i: number) => i !== index);
        setLocalConditions(newConds);
        onUpdate('conditions', newConds);
    };

    const updateCondition = (index: number, field: string, value: any) => {
        const newConds = [...localConditions];
        newConds[index][field] = value;

        // Auto-init fuzzy config
        if (field === 'operator' && value === 'SIMILAR_TO' && !newConds[index].fuzzyConfig) {
            newConds[index].fuzzyConfig = { algorithm: 'jaccard', threshold: 0.8 };
        }

        setLocalConditions(newConds);
        onUpdate('conditions', newConds);
    };

    // Local update handlers - sync to parent in real-time on every change
    const updateLocalJoinType = (type: string) => {
        setLocalJoinType(type);
        onUpdate('joinType', type);
    };

    const updateLocalTemporalJoin = (updates: any) => {
        const merged = { ...localTemporalJoin, ...updates };
        setLocalTemporalJoin(merged);
        onUpdate('temporalJoin', merged);
    };

    const updateLocalRiskMonitor = (updates: any) => {
        const merged = { ...localRiskMonitor, ...updates };
        setLocalRiskMonitor(merged);
        onUpdate('riskMonitor', merged);
    };

    const updateLocalInspector = (updates: any) => {
        const merged = { ...localInspector, ...updates };
        setLocalInspector(merged);
        onUpdate('inspector', merged);
    };

    const updateLocalColumns = (cols: any) => {
        setLocalColumns(cols);
        onUpdate('columns', cols);
    };

    // Done button just closes — data is already synced to parent in real-time
    const handleClose = () => {
        onClose();
    };

    const handleAutoMap = async () => {
        setIsGenerating(true);
        try {
            const token = getToken();
            const leftCols = getColsForSide('left').map(c => c.name);
            const rightCols = getColsForSide('right').map(c => c.name);

            if (leftCols.length === 0 || rightCols.length === 0) {
                notify.error("Both input sources must have columns for auto-mapping.");
                setIsGenerating(false);
                return;
            }

            // Corrected prefix to include /api/v1 if needed, but the backend search failed for suggest-join.
            // Using /datasources or /datamodels pattern.
            const url = `${API_BASE_URL}/ai/suggest-join`;
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    leftColumns: leftCols,
                    rightColumns: rightCols
                })
            });

            if (res.ok) {
                const data = await res.json();
                if (data.suggestion && Array.isArray(data.suggestion)) {
                    // Map suggestions to condition structure
                    const newConds = data.suggestion.map((s: any) => ({
                        leftCol: s.leftKey,
                        operator: '=',
                        rightCol: s.rightKey
                    }));

                    if (newConds.length > 0) {
                        setLocalConditions([...localConditions, ...newConds]);
                        notify.success(`Found ${newConds.length} join keys!`);
                    } else {
                        notify.info("No obvious join keys found.");
                    }
                }
            } else {
                notify.error("Failed to fetch suggestions.");
            }
        } catch (e) {
            console.error(e);
            notify.error("Error auto-mapping columns.");
        } finally {
            setIsGenerating(false);
        }
    };

    // All hooks are above this point. Safe to return null after this guard.
    if (!isOpen) return null;

    const JoinTypeCard = ({ type, icon: Icon, label, desc }: any) => {
        const isSelected = localJoinType === type;
        return (
            <button
                className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 relative overflow-hidden group
                    ${isSelected
                        ? 'border-purple-600 bg-purple-50 text-purple-900 shadow-md transform scale-[1.02]'
                        : 'border-slate-100 bg-white text-slate-500 hover:border-slate-200 hover:shadow-sm'
                    }
                `}
                onClick={() => updateLocalJoinType(type)}
            >
                {isSelected && (
                    <div className="absolute top-2 right-2 bg-purple-600 text-white p-0.5 rounded-full">
                        <Check className="h-3 w-3" />
                    </div>
                )}
                <div className={`p-3 rounded-full mb-3 ${isSelected ? 'bg-purple-200' : 'bg-slate-100 group-hover:bg-slate-200'}`}>
                    <Icon className={`h-6 w-6 ${isSelected ? 'text-purple-700' : 'text-slate-400'}`} />
                </div>
                <h3 className="font-bold text-sm mb-1">{label}</h3>
                <p className="text-[10px] text-center opacity-80 leading-tight">{desc}</p>
            </button>
        );
    };

    // Early-return AFTER all hooks (Rules of Hooks: no early returns before hooks)
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-[1200px] max-w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-white/20">

                {/* Header */}
                <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                            <GitMerge className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">Configure Join Logic</h2>
                            <p className="text-xs text-slate-500">Combine data from two upstream sources.</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50/30">

                    {/* Section 1: Join Type */}
                    <section>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 block">1. Choose Join Type</label>
                        <div className="flex gap-2 justify-between">
                            <JoinTypeCard
                                type="INNER"
                                icon={Circle} // Conceptual: Intersection 
                                label="Inner Join"
                                desc="Keep only rows that match in BOTH tables."
                            />
                            <JoinTypeCard
                                type="LEFT"
                                icon={ArrowLeft}
                                label="Left Join"
                                desc="Keep ALL Left rows + matching Right rows."
                            />
                            <JoinTypeCard
                                type="RIGHT"
                                icon={ArrowRight}
                                label="Right Join"
                                desc="Keep ALL Right rows + matching Left rows."
                            />
                            <JoinTypeCard
                                type="FULL"
                                icon={ArrowLeftRight}
                                label="Full Join"
                                desc="Keep EVERYTHING from both tables."
                            />
                            <JoinTypeCard
                                type="CROSS"
                                icon={X}
                                label="Cross Join"
                                desc="Cartesian Product (Every row × Every row)"
                            />
                        </div>
                    </section>

                    {/* Section 2: Match Conditions */}
                    <section className="pt-4 border-t border-slate-200/60">
                        <div className="flex items-center justify-between mb-4">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">2. Match Conditions</label>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" className="h-6 text-xs text-purple-700 border-purple-200 hover:bg-purple-50" onClick={handleAddCondition}>
                                    <Plus className="h-3 w-3 mr-1" /> Add Condition
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-xs text-purple-700 border-purple-200 hover:bg-purple-50"
                                    onClick={handleAutoMap}
                                    disabled={isGenerating}
                                >
                                    {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3 mr-1" />}
                                    Auto Map
                                </Button>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                            {/* Column headers — aligned with condition row flex layout */}
                            {localConditions.length > 0 && (
                                <div className="flex items-center gap-3 px-3 pt-2 pb-1 border-b border-slate-100 bg-slate-50/60">
                                    <div className="flex-1">
                                        <span className="text-[11px] text-purple-600 font-bold uppercase tracking-wide">Left Source: {leftSourceName}</span>
                                    </div>
                                    <div className="w-[140px]" />
                                    <div className="flex-1">
                                        <span className="text-[11px] text-blue-600 font-bold uppercase tracking-wide">Right Source: {rightSourceName}</span>
                                    </div>
                                    <div className="w-8" />{/* spacer for trash button */}
                                </div>
                            )}
                            {localConditions.length === 0 ? (
                                <div className="p-8 text-center text-slate-400 text-sm">
                                    No conditions set. This will result in a Cartesian Product (Cross Join).
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100">
                                    {localConditions.map((cond: any, i: number) => (
                                        <div key={i} className="p-3 bg-white hover:bg-slate-50/50 transition-colors group">
                                            <div className="flex items-center gap-3">
                                                {/* Left Col */}
                                                <div className="flex-1">
                                                    <Select value={cond.leftCol} onValueChange={(v) => updateCondition(i, 'leftCol', v)}>
                                                        <SelectTrigger className="h-9 text-sm border-slate-200 bg-slate-50 focus:bg-white focus:ring-purple-500">
                                                            <SelectValue placeholder="Left Column..." />
                                                        </SelectTrigger>
                                                        <SelectContent className="z-[10001] bg-white">
                                                            {getColsForSide('left').filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map((c: any) => (
                                                                <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {/* Operator */}
                                                <div className="w-[140px] flex flex-col justify-end">
                                                    <Select value={cond.operator} onValueChange={(v) => updateCondition(i, 'operator', v)}>
                                                        <SelectTrigger className={`h-9 text-sm font-mono text-center font-bold ${cond.operator === 'SIMILAR_TO' ? 'text-purple-600 bg-purple-50 border-purple-200' : 'text-slate-600 bg-white border-slate-200'}`}>
                                                            <SelectValue placeholder="Op" />
                                                        </SelectTrigger>
                                                        <SelectContent className="z-[10001] bg-white">
                                                            <SelectItem value="=">=</SelectItem>
                                                            <SelectItem value="!=">!= (Not Equal)</SelectItem>
                                                            <SelectItem value=">">&gt; (Greater)</SelectItem>
                                                            <SelectItem value="<">&lt; (Less)</SelectItem>
                                                            <SelectItem value=">=">&gt;= (GTE)</SelectItem>
                                                            <SelectItem value="<=">&lt;= (LTE)</SelectItem>
                                                            <SelectItem value="SIMILAR_TO">≈ (Fuzzy)</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {/* Right Col */}
                                                <div className="flex-1">
                                                    <Select value={cond.rightCol} onValueChange={(v) => updateCondition(i, 'rightCol', v)}>
                                                        <SelectTrigger className="h-9 text-sm border-slate-200 bg-slate-50 focus:bg-white focus:ring-purple-500">
                                                            <SelectValue placeholder="Right Column..." />
                                                        </SelectTrigger>
                                                        <SelectContent className="z-[10001] bg-white">
                                                            {getColsForSide('right').filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map((c: any) => (
                                                                <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {/* Actions */}
                                                <button
                                                    onClick={() => handleRemoveCondition(i)}
                                                    className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>

                                            {/* Advanced Config Area (Fuzzy) */}
                                            {cond.operator === 'SIMILAR_TO' && (
                                                <div className="mt-3 ml-1 p-3 bg-purple-50/50 rounded-lg border border-purple-100 flex items-center gap-4 animate-in slide-in-from-top-2">
                                                    <div className="flex items-center gap-2 text-purple-800 text-xs font-semibold">
                                                        <Sparkles className="h-3 w-3" />
                                                        Fuzzy Settings
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] text-purple-600 uppercase font-bold">Algorithm</span>
                                                        <select
                                                            className="h-6 text-xs rounded border-purple-200 text-purple-700 bg-white focus:ring-purple-500"
                                                            value={cond.fuzzyConfig?.algorithm || 'jaccard'}
                                                            onChange={(e) => updateCondition(i, 'fuzzyConfig', { ...cond.fuzzyConfig, algorithm: e.target.value })}
                                                        >
                                                            <option value="jaccard">Jaccard (Text Similiarity)</option>
                                                            <option value="levenshtein">Levenshtein (Edit Distance)</option>
                                                        </select>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] text-purple-600 uppercase font-bold">Threshold ({cond.fuzzyConfig?.threshold || 0.8})</span>
                                                        <input
                                                            type="range" min="0" max="1" step="0.1"
                                                            className="w-24 accent-purple-600"
                                                            value={cond.fuzzyConfig?.threshold || 0.8}
                                                            onChange={(e) => updateCondition(i, 'fuzzyConfig', { ...cond.fuzzyConfig, threshold: parseFloat(e.target.value) })}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Section 3: Advanced Toggles */}
                    <section className="pt-4 border-t border-slate-200/60">
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 block">3. Advanced Logic</label>

                        <div className="grid grid-cols-2 gap-4">
                            {/* Temporal Toggle */}
                            <div className={`p-4 rounded-xl border-2 transition-all ${temporalJoin.enabled ? 'border-blue-200 bg-blue-50/50' : 'border-slate-100 bg-white'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-1.5 rounded-lg ${localTemporalJoin.enabled ? 'bg-blue-200 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                                            <Calendar className="h-4 w-4" />
                                        </div>
                                        <span className={`font-semibold text-sm ${localTemporalJoin.enabled ? 'text-blue-900' : 'text-slate-500'}`}>Time-Travel Join</span>
                                    </div>
                                    <input
                                        type="checkbox"
                                        className="h-5 w-5 rounded border-2 border-slate-300 text-blue-600 focus:ring-blue-500"
                                        checked={localTemporalJoin.enabled || false}
                                        onChange={(e) => updateLocalTemporalJoin({ enabled: e.target.checked })}
                                    />
                                </div>

                                {localTemporalJoin.enabled && (
                                    <div className="mt-3 space-y-3 animate-in fade-in">
                                        <div className="space-y-1">
                                            <label className="text-[10px] text-blue-600 font-bold uppercase">Transaction Date (Left)</label>
                                            <Select value={localTemporalJoin.leftDate} onValueChange={(v) => updateLocalTemporalJoin({ leftDate: v })}>
                                                <SelectTrigger className="h-8 text-xs bg-white border-blue-200">
                                                    <SelectValue placeholder="Event Date..." />
                                                </SelectTrigger>
                                                <SelectContent className="z-[10001] bg-white">
                                                    {getColsForSide('left').map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <label className="text-[10px] text-blue-600 font-bold uppercase">Effective From (Right)</label>
                                                <Select value={localTemporalJoin.rightStart} onValueChange={(v) => updateLocalTemporalJoin({ rightStart: v })}>
                                                    <SelectTrigger className="h-8 text-xs bg-white border-blue-200">
                                                        <SelectValue placeholder="Start Date..." />
                                                    </SelectTrigger>
                                                    <SelectContent className="z-[10001] bg-white">
                                                        {getColsForSide('right').map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] text-blue-600 font-bold uppercase">Effective To (Right)</label>
                                                <Select value={localTemporalJoin.rightEnd} onValueChange={(v) => updateLocalTemporalJoin({ rightEnd: v })}>
                                                    <SelectTrigger className="h-8 text-xs bg-white border-blue-200">
                                                        <SelectValue placeholder="End Date (Opt)..." />
                                                    </SelectTrigger>
                                                    <SelectContent className="z-[10001] bg-white">
                                                        {getColsForSide('right').map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Deduplication Toggle (1:1 Force) */}
                            <div className={`p-4 rounded-xl border-2 transition-all ${nodeData.deduplicateRight ? 'border-cyan-200 bg-cyan-50/50' : 'border-slate-100 bg-white'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-1.5 rounded-lg ${nodeData.deduplicateRight ? 'bg-cyan-200 text-cyan-700' : 'bg-slate-100 text-slate-400'}`}>
                                            <GitMerge className="h-4 w-4 rotate-180" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className={`font-semibold text-sm ${nodeData.deduplicateRight ? 'text-cyan-900' : 'text-slate-500'}`}>Keep First Match Only</span>
                                            <span className="text-[9px] text-slate-400 leading-none">Deduplicate Right Side (1:1)</span>
                                        </div>
                                    </div>
                                    <input
                                        type="checkbox"
                                        className="h-5 w-5 rounded border-2 border-slate-300 text-cyan-600 focus:ring-cyan-500"
                                        checked={nodeData.deduplicateRight || false}
                                        onChange={(e) => onUpdate('deduplicateRight', e.target.checked)}
                                    />
                                </div>

                                {nodeData.deduplicateRight && (
                                    <div className="mt-3 p-3 bg-white/60 rounded border border-cyan-100 animate-in fade-in">
                                        <p className="text-[10px] text-cyan-800 leading-relaxed">
                                            <span className="font-bold">⚠️ Data Loss Warning:</span>
                                            <br />
                                            If multiple rows match from the Right table, only the <b>first one</b> found will be kept.
                                            This ensures the Left row count remains constant (1000).
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Placeholder for future innovation: Semantic Join */}
                            {/* Value at Risk Monitor */}
                            <div className={`p-4 rounded-xl border-2 transition-all ${localRiskMonitor.enabled ? 'border-amber-200 bg-amber-50/50' : 'border-slate-100 bg-white'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-1.5 rounded-lg ${localRiskMonitor.enabled ? 'bg-amber-200 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>
                                            <AlertTriangle className="h-4 w-4" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className={`font-semibold text-sm ${localRiskMonitor.enabled ? 'text-amber-900' : 'text-slate-500'}`}>Value at Risk</span>
                                            <span className="text-[9px] text-slate-400 leading-none">Monitor financial impact of drops</span>
                                        </div>
                                    </div>
                                    <input
                                        type="checkbox"
                                        className="h-5 w-5 rounded border-2 border-slate-300 text-amber-600 focus:ring-amber-500"
                                        checked={localRiskMonitor.enabled || false}
                                        onChange={(e) => updateLocalRiskMonitor({ enabled: e.target.checked })}
                                    />
                                </div>

                                {localRiskMonitor.enabled && (
                                    <div className="mt-3 space-y-3 animate-in fade-in">
                                        <div className="space-y-1">
                                            <label className="text-[10px] text-amber-700 font-bold uppercase">Value Column (Left)</label>
                                            <Select value={localRiskMonitor.valueCol} onValueChange={(v) => updateLocalRiskMonitor({ valueCol: v })}>
                                                <SelectTrigger className="h-8 text-xs bg-white border-amber-200">
                                                    <SelectValue placeholder="e.g. Amount, Revenue..." />
                                                </SelectTrigger>
                                                <SelectContent className="z-[10001] bg-white">
                                                    {getColsForSide('left').map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] text-amber-700 font-bold uppercase">Risk Threshold (%)</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="range" min="1" max="50" step="1"
                                                    className="flex-1 accent-amber-600"
                                                    value={localRiskMonitor.threshold || 10}
                                                    onChange={(e) => updateLocalRiskMonitor({ threshold: parseInt(e.target.value) })}
                                                />
                                                <span className="text-xs font-mono font-bold text-amber-800 w-8 text-right">{localRiskMonitor.threshold || 10}%</span>
                                            </div>
                                            <p className="text-[9px] text-amber-600/70 italic">
                                                Alert if lost value exceeds {localRiskMonitor.threshold || 10}% of total.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* "Why Not?" Inspector */}
                            <div className={`p-4 rounded-xl border-2 transition-all ${localInspector.enabled ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-100 bg-white'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-1.5 rounded-lg ${localInspector.enabled ? 'bg-indigo-200 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>
                                            <HelpCircle className="h-4 w-4" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className={`font-semibold text-sm ${localInspector.enabled ? 'text-indigo-900' : 'text-slate-500'}`}>"Why Not?" Inspector</span>
                                            <span className="text-[9px] text-slate-400 leading-none">Debug missing/dropped rows</span>
                                        </div>
                                    </div>
                                    <input
                                        type="checkbox"
                                        className="h-5 w-5 rounded border-2 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={localInspector.enabled || false}
                                        onChange={(e) => updateLocalInspector({ enabled: e.target.checked })}
                                    />
                                </div>

                                {localInspector.enabled && (
                                    <div className="mt-3 p-3 bg-white/60 rounded border border-indigo-100 animate-in fade-in">
                                        <p className="text-[10px] text-indigo-800 leading-relaxed">
                                            <span className="font-bold">✨ Magic Debugger Enabled:</span>
                                            <br />
                                            The system will generate a <span className="font-mono bg-indigo-100 px-1 rounded">Lost & Found</span> report.
                                            Click any dropped row to see exactly which condition failed (e.g. "ID 1024 missing in Right Table").
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Section 4: Output Columns (New) */}
                    <section className="pt-4 border-t border-slate-200/60 flex-1 min-h-0 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">4. Output Columns</label>
                            <div className="flex items-center gap-2">
                                <Search className="h-4 w-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Filter columns..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="text-xs border-none bg-transparent focus:ring-0 text-slate-600 placeholder:text-slate-300 outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex-1 grid grid-cols-2 gap-6 min-h-0 overflow-hidden">
                            {/* Left Columns */}
                            <div className="flex flex-col gap-2 min-h-0">
                                <div className="text-xs font-semibold text-purple-700 bg-purple-50 px-3 py-2 rounded-lg border border-purple-100 flex justify-between shrink-0">
                                    <span>Left Source: {leftSourceName}</span>
                                    <span className="opacity-50">{leftColumns.length} cols</span>
                                </div>
                                <div className="flex-1 overflow-y-auto bg-white rounded-xl border border-slate-200 shadow-sm p-1 space-y-1">
                                    {getColsForSide('left').filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map((col: any) => {
                                        const config = (localColumns || []).find((c: any) => c.name === col.name) || { keep: true };
                                        return (
                                            <div key={col.name} className="group flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={config.keep !== false}
                                                    onChange={(e) => {
                                                        const currentCols = localColumns || inputColumns.map(c => ({ name: c.name, keep: true }));
                                                        const idx = currentCols.findIndex((c: any) => c.name === col.name);
                                                        if (idx >= 0) {
                                                            currentCols[idx] = { ...currentCols[idx], keep: e.target.checked };
                                                        } else {
                                                            currentCols.push({ name: col.name, keep: e.target.checked });
                                                        }
                                                        updateLocalColumns(currentCols);
                                                    }}
                                                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-medium text-slate-700 truncate" title={col.name}>{col.name}</div>
                                                    <div className="text-[10px] text-slate-400 truncate">{col.type}</div>
                                                </div>
                                                <div className="w-1/3 opacity-40 group-hover:opacity-100 transition-opacity">
                                                    <input
                                                        type="text"
                                                        placeholder="Alias..."
                                                        value={config.alias || ''}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            const currentCols = nodeData.columns || inputColumns.map(c => ({ name: c.name, keep: true }));
                                                            const idx = currentCols.findIndex((c: any) => c.name === col.name);
                                                            if (idx >= 0) {
                                                                currentCols[idx] = { ...currentCols[idx], alias: e.target.value };
                                                            } else {
                                                                currentCols.push({ name: col.name, alias: e.target.value, keep: true });
                                                            }
                                                            onUpdate('columns', currentCols);
                                                        }}
                                                        className="w-full text-[10px] px-2 py-1 rounded border border-slate-200 focus:border-purple-500 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right Columns */}
                            <div className="flex flex-col gap-2 min-h-0">
                                <div className="text-xs font-semibold text-blue-700 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100 flex justify-between shrink-0">
                                    <span>Right Source: {rightSourceName}</span>
                                    <span className="opacity-50">{rightColumns.length} cols</span>
                                </div>
                                <div className="flex-1 overflow-y-auto bg-white rounded-xl border border-slate-200 shadow-sm p-1 space-y-1">
                                    {getColsForSide('right').filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map((col: any) => {
                                        const config = (localColumns || []).find((c: any) => c.name === col.name) || { keep: true };
                                        return (
                                            <div key={col.name} className="group flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={config.keep !== false}
                                                    onChange={(e) => {
                                                        const currentCols = localColumns || inputColumns.map(c => ({ name: c.name, keep: true }));
                                                        const idx = currentCols.findIndex((c: any) => c.name === col.name);
                                                        if (idx >= 0) {
                                                            currentCols[idx] = { ...currentCols[idx], keep: e.target.checked };
                                                        } else {
                                                            currentCols.push({ name: col.name, keep: e.target.checked });
                                                        }
                                                        updateLocalColumns(currentCols);
                                                    }}
                                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-medium text-slate-700 truncate" title={col.name}>{col.name}</div>
                                                    <div className="text-[10px] text-slate-400 truncate">{col.type}</div>
                                                </div>
                                                <div className="w-1/3 opacity-40 group-hover:opacity-100 transition-opacity">
                                                    <input
                                                        type="text"
                                                        placeholder="Alias..."
                                                        value={config.alias || ''}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            const currentCols = localColumns || inputColumns.map(c => ({ name: c.name, keep: true }));
                                                            const idx = currentCols.findIndex((c: any) => c.name === col.name);
                                                            if (idx >= 0) {
                                                                currentCols[idx] = { ...currentCols[idx], alias: e.target.value };
                                                            } else {
                                                                currentCols.push({ name: col.name, alias: e.target.value, keep: true });
                                                            }
                                                            updateLocalColumns(currentCols);
                                                        }}
                                                        className="w-full text-[10px] px-2 py-1 rounded border border-slate-200 focus:border-blue-500 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Footer - Fixed at bottom */}
                <div className="shrink-0 p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600 gap-2" onClick={onDebugSql}>
                        <Database className="h-4 w-4" />
                        <span className="text-xs font-mono">Debug SQL</span>
                    </Button>
                    <Button onClick={handleClose} className="bg-slate-900 text-white hover:bg-slate-800 px-8">
                        Done
                    </Button>
                </div>
            </div>
        </div>
    );
};


